/**
 * Équipe — détail à onglets (217:4531, T2/T3/T4) : Membres · Adhésion · Projets, l'onglet actif
 * dans l'URL (`?onglet=`, même convention que `OrganizationPage`). La zone de danger
 * (« Supprimer l'équipe ») vit dans Adhésion, avec le transfert de propriété et « Quitter
 * l'équipe » — plus dans l'en-tête. La colonne latérale (`ASIDE`) de l'ancienne page a
 * disparu : `/equipes` ne fait plus qu'orienter (`TeamsPage`), cette page gère.
 */

import { useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roleAtLeast, TEAM_ROLES } from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { appAlert, appConfirm } from '../dialogs/service.js';
import {
  Empty,
  LoadError,
  PageBody,
  PageHeader,
  PrimaryButton,
  StatusTag,
  TruncatedListWarning,
} from '../components/common.jsx';
import { Modal } from '../components/Modal.jsx';
import { ProjectForm } from '../components/ProjectForm.jsx';
import { RenameTeamDialog } from '../dialogs/RenameTeamDialog.jsx';
import { OrgInvitations } from '../components/OrgInvitations.jsx';
import { TeamSwitcher } from '../components/TeamSwitcher.jsx';
import { TEAM_ROLE_KEYS } from '../lib/roleLabels.js';
import {
  BTN_DARK_SM,
  BTN_GHOST,
  BTN_GHOST_SM,
  CELLS_PLANCHE,
  CELL_PLANCHE,
  CHECK,
  COL_ACTIONS,
  COL_ROLE,
  EYEBROW,
  FORM_FIELD,
  FORM_LABEL,
  FORM_ROW,
  INPUT,
  SELECT,
  SELECT_SM,
  TAB_ACTIVE,
  TAB_IDLE,
  TD,
  TH,
} from '../ui/theme.js';

const TABS = ['membres', 'adhesion', 'projets'];
/** Littéraux dans l'objet — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const TAB_LABEL_KEYS = {
  membres: 'teams.tabs.members',
  adhesion: 'teams.tabs.membership',
  projets: 'teams.tabs.projects',
};

export function TeamDetailPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [memberEmail, setMemberEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [submittingProject, setSubmittingProject] = useState(false);
  const inviteEmailRef = useRef(null);

  const activeTab = TABS.includes(searchParams.get('onglet'))
    ? searchParams.get('onglet')
    : 'membres';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('onglet', tab);
    setSearchParams(next, { replace: true });
  };

  const {
    data: teamData,
    error: teamError,
    loading: teamLoading,
    reload: reloadTeam,
  } = useAsync((signal) => api.get(`/teams/${teamId}`, { signal }), [teamId]);
  const team = teamData?.data;
  const canManage = roleAtLeast(team?.role, 'admin');
  // I3 : l'espace personnel est solo (ORG-2) — le serveur y refuse tout ajout et toute
  // invitation (409 PERSONAL_SPACE). Ces gestes ne sont donc pas proposés.
  const isPersonal = team?.organizationKind === 'personnel';
  const isOwner = team?.role === 'owner';
  const assignableRoles = isOwner ? TEAM_ROLES : TEAM_ROLES.filter((role) => role !== 'owner');
  const NO_RIGHTS_TITLE = t('teams.noRightsTitle');

  const {
    data: membersData,
    error: membersError,
    reload: reloadMembers,
  } = useAsync((signal) => fetchAllPages(`/teams/${teamId}/members`, { signal }), [teamId]);
  const members = membersData?.data ?? [];

  const {
    data: projectsData,
    error: projectsError,
    loading: projectsLoading,
    reload: reloadProjects,
  } = useAsync((signal) => fetchAllPages(`/teams/${teamId}/projects`, { signal }), [teamId]);
  const projects = projectsData?.data ?? [];

  if (teamLoading) {
    return (
      <div>
        <PageHeader eyebrow={t('teams.backToList')} title={t('teams.loading')} />
      </div>
    );
  }

  // 404 = équipe inconnue ou inaccessible ; toute autre erreur est un échec de chargement,
  // annoncé comme tel avec « Réessayer » (« introuvable » serait faux sur une panne).
  if (teamError && teamError.status !== 404) {
    return (
      <div>
        <PageHeader eyebrow={t('teams.backToList')} title={t('teams.title')} />
        <PageBody className="p-4">
          <LoadError error={teamError} onRetry={reloadTeam} />
        </PageBody>
      </div>
    );
  }

  if (teamError || !team) {
    return (
      <div>
        <PageHeader eyebrow={t('teams.backToList')} title={t('teams.notFoundTitle')} />
        <PageBody className="p-4">
          <Empty>{t('teams.notFound')}</Empty>
        </PageBody>
      </div>
    );
  }

  const addMember = async (event) => {
    event.preventDefault();
    if (!memberEmail.trim()) return;
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/members`, { email: memberEmail.trim() });
      setMemberEmail('');
      reloadMembers();
    } catch (error) {
      await appAlert(t('teams.addFailed', { message: error.message }), {
        title: t('teams.addFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (member, role) => {
    setBusy(true);
    try {
      await api.patch(`/teams/${teamId}/members/${member.userId}`, {
        role,
        canDelete: member.canDelete,
      });
      reloadMembers();
    } catch (error) {
      await appAlert(t('teams.roleChangeFailed', { message: error.message }), {
        title: t('teams.roleChangeTitle'),
      });
      reloadMembers();
    } finally {
      setBusy(false);
    }
  };

  const toggleCanDelete = async (member) => {
    setBusy(true);
    try {
      await api.patch(`/teams/${teamId}/members/${member.userId}`, {
        role: member.role,
        canDelete: !member.canDelete,
      });
      reloadMembers();
    } catch (error) {
      await appAlert(t('teams.canDeleteChangeFailed', { message: error.message }), {
        title: t('teams.roleChangeTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (member) => {
    if (
      !(await appConfirm(t('teams.removeConfirm', { name: member.name || member.email }), {
        confirmLabel: t('teams.removeConfirmLabel'),
      }))
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/teams/${teamId}/members/${member.userId}`);
      reloadMembers();
    } catch (error) {
      await appAlert(t('teams.removeFailed', { message: error.message }), {
        title: t('teams.removeFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const openRename = () => setRenaming(true);

  const createProject = async (values) => {
    setSubmittingProject(true);
    try {
      await api.post('/projects', { ...values, teamId });
      setCreatingProject(false);
      reloadProjects();
    } catch (error) {
      // Même refus attendu que sur `/projets` (clé déjà prise dans l'équipe, 409) : la modale
      // reste ouverte pour corriger la saisie.
      await appAlert(t('projects.createFailed', { message: error.message }), {
        title: t('teams.newProject'),
      });
    } finally {
      setSubmittingProject(false);
    }
  };

  const focusInvitation = () => {
    const field = inviteEmailRef.current;
    if (!field) return;
    field.scrollIntoView?.({ block: 'center' });
    field.focus();
  };

  /*
   * LE primaire de l'écran (règle de cohérence 1.3.0 d'`@exidis/ui`), à droite de « Renommer » :
   * « Inviter un membre » sur Membres (amène au formulaire d'invitation, qui garde son « Inviter »
   * fantôme), « Nouveau projet » sur Projets. Adhésion n'a pas d'action principale. Même
   * convention que « Renommer » quand le droit manque : visible, désactivé, avec la raison.
   */
  const canCreateProject = roleAtLeast(team.role, 'member');
  let primaryAction = null;
  if (activeTab === 'membres' && !isPersonal) {
    primaryAction = (
      <PrimaryButton
        onClick={focusInvitation}
        disabled={!canManage}
        title={!canManage ? NO_RIGHTS_TITLE : undefined}
      >
        {t('teams.inviteMember')}
      </PrimaryButton>
    );
  } else if (activeTab === 'projets') {
    primaryAction = (
      <PrimaryButton
        onClick={() => setCreatingProject(true)}
        disabled={!canCreateProject}
        title={!canCreateProject ? NO_RIGHTS_TITLE : undefined}
      >
        {t('teams.newProject')}
      </PrimaryButton>
    );
  }

  const deleteTeam = async () => {
    if (
      !(await appConfirm(t('teams.deleteConfirm', { name: team.name }), {
        confirmLabel: t('teams.deleteConfirmLabel'),
      }))
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/teams/${teamId}`);
      navigate('/equipes');
    } catch (error) {
      await appAlert(t('teams.deleteFailed', { message: error.message }), {
        title: t('teams.deleteFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  const leaveTeam = async () => {
    if (
      !(await appConfirm(t('teams.leaveConfirm', { name: team.name }), {
        confirmLabel: t('teams.leaveConfirmLabel'),
      }))
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/teams/${teamId}/members/me`);
      navigate('/equipes');
    } catch (error) {
      await appAlert(error.message, { title: t('teams.leaveFailedTitle') });
    } finally {
      setBusy(false);
    }
  };

  const transferTeamOwnership = async () => {
    if (!transferTargetId) return;
    const target = members.find((member) => member.userId === transferTargetId);
    if (
      !(await appConfirm(
        t('teams.transferConfirm', { name: target?.name || target?.email || transferTargetId }),
        { confirmLabel: t('teams.transferConfirmLabel') },
      ))
    )
      return;
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/transfer-ownership`, { userId: transferTargetId });
      setTransferTargetId('');
      await reloadTeam();
      reloadMembers();
    } catch (error) {
      await appAlert(t('teams.transferFailed', { message: error.message }), {
        title: t('teams.transferFailedTitle'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={
          <Link to="/equipes" className="hover:text-accent-ink">
            {t('teams.backToList')}
          </Link>
        }
        title={<TeamSwitcher teamId={teamId} name={team.name} />}
        actions={
          <button
            type="button"
            className={BTN_GHOST}
            onClick={openRename}
            disabled={!canManage}
            title={!canManage ? NO_RIGHTS_TITLE : undefined}
          >
            {t('teams.rename')}
          </button>
        }
        primary={primaryAction}
      />

      <div className="border-b-2 border-line">
        <nav
          className="mx-auto flex w-full max-w-(--largeur-corps) items-end gap-1 px-4 pt-2"
          aria-label={t('teams.tabsAriaLabel')}
        >
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={activeTab === tab ? TAB_ACTIVE : TAB_IDLE}
              aria-current={activeTab === tab ? 'page' : undefined}
              onClick={() => setActiveTab(tab)}
            >
              {t(TAB_LABEL_KEYS[tab])}
            </button>
          ))}
        </nav>
      </div>

      <PageBody className="p-4">
        {activeTab === 'membres' && membersError && (
          <LoadError error={membersError} onRetry={reloadMembers} className="mb-3" />
        )}
        {activeTab === 'membres' && membersData?.truncated && (
          <div className="mb-3">
            <TruncatedListWarning />
          </div>
        )}
        {activeTab === 'projets' && projectsError && (
          <LoadError error={projectsError} onRetry={reloadProjects} className="mb-3" />
        )}
        {activeTab === 'projets' && projectsData?.truncated && (
          <div className="mb-3">
            <TruncatedListWarning />
          </div>
        )}
        {activeTab === 'membres' && (
          <div className={`${CELLS_PLANCHE} grid-cols-1`}>
            <div className={`${CELL_PLANCHE} bg-paper p-6`}>
              <span className="ticks" aria-hidden="true" />

              {/* Ligne de formulaire MD (reference.md §6.0) : champ et bouton à 32 px, comme la
                  ligne « Inviter un membre » plus bas. Le tableau, lui, est SM de bout en bout. */}
              {isPersonal ? (
                <p className="text-sm text-ink-soft">{t('teams.personalSpaceSolo')}</p>
              ) : (
                <form onSubmit={addMember} className={FORM_ROW}>
                  <input
                    aria-label={t('teams.memberEmailAria')}
                    className={`${INPUT} min-w-56`}
                    placeholder={t('teams.memberEmailPlaceholder')}
                    value={memberEmail}
                    onChange={(event) => setMemberEmail(event.target.value)}
                    disabled={!canManage}
                    title={!canManage ? NO_RIGHTS_TITLE : undefined}
                  />
                  <button
                    type="submit"
                    className={BTN_GHOST}
                    disabled={!canManage || busy}
                    title={!canManage ? NO_RIGHTS_TITLE : undefined}
                  >
                    {t('teams.add')}
                  </button>
                </form>
              )}

              <table className="mt-3 w-full table-fixed border-collapse">
                <colgroup>
                  <col className="w-auto" />
                  <col className={COL_ROLE} />
                  <col className="w-[120px]" />
                  <col className={COL_ACTIONS} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={TH}>{t('teams.member')}</th>
                    <th className={TH}>{t('teams.role')}</th>
                    <th className={TH}>{t('teams.canDelete')}</th>
                    <th className={TH}>{t('teams.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.userId}>
                      <td className={TD}>
                        <span className="block text-ink">{member.name || member.email}</span>
                        {member.name && member.email && (
                          <span className="block font-mono text-[11px] text-ink-faint">
                            {member.email}
                          </span>
                        )}
                      </td>
                      <td className={TD}>
                        <select
                          aria-label={t('teams.roleAria', { name: member.name || member.email })}
                          className={SELECT_SM}
                          value={member.role}
                          onChange={(event) => changeRole(member, event.target.value)}
                          disabled={!canManage}
                          title={!canManage ? NO_RIGHTS_TITLE : undefined}
                        >
                          {TEAM_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {t(TEAM_ROLE_KEYS[role])}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={TD}>
                        <input
                          className={CHECK}
                          type="checkbox"
                          aria-label={t('teams.canDeleteAria', {
                            name: member.name || member.email,
                          })}
                          checked={Boolean(member.canDelete)}
                          onChange={() => toggleCanDelete(member)}
                          disabled={!canManage}
                          title={!canManage ? NO_RIGHTS_TITLE : undefined}
                        />
                      </td>
                      <td className={TD}>
                        <button
                          type="button"
                          className={BTN_GHOST_SM}
                          onClick={() => removeMember(member)}
                          disabled={!canManage}
                          title={!canManage ? NO_RIGHTS_TITLE : undefined}
                        >
                          {t('teams.remove')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {canManage && !isPersonal && (
                <div className="mt-4">
                  <OrgInvitations
                    scope="team"
                    scopeId={teamId}
                    assignableRoles={assignableRoles}
                    emailInputRef={inviteEmailRef}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'adhesion' && (
          <div className={`${CELLS_PLANCHE} grid-cols-1 sm:grid-cols-2`}>
            <div className={`${CELL_PLANCHE} bg-paper p-6`}>
              <span className="ticks" aria-hidden="true" />
              <section className="flex flex-col items-start gap-3">
                <p className={EYEBROW}>{t('teams.membershipTitle')}</p>
                {isOwner && (
                  <div className={FORM_ROW}>
                    <label className={FORM_FIELD}>
                      <span className={FORM_LABEL}>{t('teams.transferLabel')}</span>
                      <select
                        className={SELECT}
                        value={transferTargetId}
                        onChange={(event) => setTransferTargetId(event.target.value)}
                        disabled={busy}
                      >
                        <option value="">{t('teams.transferPlaceholder')}</option>
                        {members
                          .filter((member) => member.userId !== user?.id)
                          .map((member) => (
                            <option key={member.userId} value={member.userId}>
                              {member.name || member.email}
                            </option>
                          ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className={BTN_GHOST}
                      disabled={busy || !transferTargetId}
                      onClick={transferTeamOwnership}
                    >
                      {t('teams.transfer')}
                    </button>
                  </div>
                )}
                <p className="text-xs text-ink-faint">{t('teams.transferWarning')}</p>
                <button type="button" className={BTN_GHOST_SM} disabled={busy} onClick={leaveTeam}>
                  {t('teams.leave')}
                </button>
              </section>
            </div>

            <div className={`${CELL_PLANCHE} bg-paper p-6`}>
              <span className="ticks" aria-hidden="true" />
              <section className="flex flex-col items-start gap-3">
                <p className={EYEBROW}>{t('teams.dangerTitle')}</p>
                <button
                  type="button"
                  className={BTN_DARK_SM}
                  onClick={deleteTeam}
                  disabled={!isOwner}
                  title={!isOwner ? NO_RIGHTS_TITLE : undefined}
                >
                  {t('teams.delete')}
                </button>
              </section>
            </div>
          </div>
        )}

        {activeTab === 'projets' && (
          <div className={`${CELLS_PLANCHE} grid-cols-1`}>
            <div className={`${CELL_PLANCHE} bg-paper p-6`}>
              <span className="ticks" aria-hidden="true" />
              <p className={EYEBROW}>{t('teams.projectsTitle')}</p>
              {projectsLoading ? (
                <p className="mt-3 text-sm text-ink-soft">{t('teams.projectsLoading')}</p>
              ) : projects.length === 0 ? (
                <div className="mt-3">
                  <Empty>{t('teams.projectsEmpty')}</Empty>
                </div>
              ) : (
                <ul className="mt-3 flex flex-col gap-2">
                  {projects.map((project) => (
                    <li
                      key={project.id}
                      className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-line-soft py-2"
                    >
                      <Link
                        to={`/projets/${project.id}`}
                        className="text-sm text-ink hover:text-accent-ink"
                      >
                        <span className="font-mono text-xs uppercase tracking-label text-ink-faint">
                          {project.key}
                        </span>{' '}
                        {project.name}
                      </Link>
                      <StatusTag status={project.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </PageBody>

      {creatingProject && (
        <Modal title={t('projects.modalTitle')} onClose={() => setCreatingProject(false)}>
          <ProjectForm
            teams={[{ ...team, id: teamId }]}
            submitting={submittingProject}
            onSubmit={createProject}
            onCancel={() => setCreatingProject(false)}
          />
        </Modal>
      )}

      {renaming && (
        <RenameTeamDialog
          teamId={teamId}
          team={team}
          onClose={() => setRenaming(false)}
          onRenamed={async () => {
            setRenaming(false);
            await reloadTeam();
          }}
        />
      )}
    </div>
  );
}
